import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Grid,
  Button,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TerrainOutlinedIcon from '@mui/icons-material/TerrainOutlined';
import { countries } from '../data/locations';
import { C } from '../theme';
import MediaPlaceholder from './MediaPlaceholder';

interface AdCardProps {
  id: number;
  title: string;
  price: number;
  country_id: string;
  city_id: string;
  thumbnail: string | null;
  land_type: string;
  area: number;
  author_name: string;
  created_at: string;
}

const landTypeLabelMap: Record<string, string> = {
  agricultural: 'Poljoprivredno zemljište',
  residential: 'Gradsko zemljište',
  commercial: 'Šumsko zemljište',
  industrial: 'Zemljište za izgradnju',
  recreational: 'Zemljište za rekreaciju',
};

const AdCard: React.FC<AdCardProps> = ({
  id,
  title,
  price,
  country_id,
  city_id,
  thumbnail,
  land_type,
  area,
  author_name,
  created_at,
}) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const hasImage = Boolean(thumbnail) && !imgError;

  const country = countries.find(c => c.id === Number(country_id));
  const city = country?.cities.find(c => c.id === Number(city_id));

  const handleClick = () => {
    navigate(`/ads/${id}`);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        backgroundColor: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: 4,
        overflow: 'hidden',
        boxShadow: 'none',
        transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: 'rgba(243,192,21,0.35)',
          boxShadow: '0 20px 44px rgba(0,0,0,0.5)',
          '& .card-image': { transform: 'scale(1.05)' },
          '& .view-button': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
      onClick={handleClick}
    >
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        {hasImage ? (
          <CardMedia
            component="img"
            height="280"
            image={`/api/ads/uploads/thumbnails/${thumbnail}`}
            alt={title}
            className="card-image"
            onError={() => setImgError(true)}
            sx={{ objectFit: 'cover', transition: 'transform 0.3s ease' }}
          />
        ) : (
          <MediaPlaceholder height={280} icon={<TerrainOutlinedIcon sx={{ fontSize: 56, color: 'rgba(243,192,21,0.45)' }} />} label="Bez fotografije" />
        )}
        {/* gradient scrim so chips stay readable over any photo */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(10,10,11,0.45) 0%, transparent 30%, transparent 70%, rgba(10,10,11,0.55) 100%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          className="view-button"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -40%)',
            opacity: 0,
            transition: 'all 0.3s ease',
            zIndex: 2,
          }}
        >
          <Button variant="contained" sx={{ px: 3, py: 1.3 }}>
            Pogledaj detalje
          </Button>
        </Box>
        <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 1 }}>
          <Chip
            label={landTypeLabelMap[land_type] || land_type}
            sx={{
              backgroundColor: 'rgba(10,10,11,0.72)',
              color: C.white,
              fontWeight: 600,
              borderRadius: 2,
              fontSize: '0.75rem',
              border: `1px solid ${C.line}`,
              backdropFilter: 'blur(10px)',
            }}
          />
        </Box>
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
          <Chip
            icon={<EuroIcon sx={{ color: `${C.onGold} !important` }} />}
            label={`${price.toLocaleString()} €`}
            sx={{
              background: C.goldGrad,
              color: C.onGold,
              fontWeight: 800,
              borderRadius: 2,
              fontSize: '0.875rem',
              boxShadow: '0 6px 18px rgba(224,162,31,0.35)',
            }}
          />
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="h5"
          component="a"
          href={`/ads/${id}`}
          sx={{
            fontWeight: 700,
            mb: 1,
            lineHeight: 1.3,
            color: C.white,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': { color: '#F6CB4B' },
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocationOnIcon sx={{ color: C.gold, mr: 0.5, fontSize: '1.2rem' }} />
          <Typography variant="body2" sx={{ color: C.muted, fontWeight: 500 }}>
            {city?.name}, {country?.name}
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
              Površina
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: C.white }}>
              {area.toLocaleString()} m²
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
              Autor
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: C.white }}>
              {author_name}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 'auto', pt: 2, borderTop: `1px solid ${C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: C.muted, fontSize: '0.75rem' }}>
            {new Date(created_at).toLocaleDateString('sr-RS')}
          </Typography>
          <ArrowForwardIcon sx={{ color: C.gold, fontSize: '1.2rem' }} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdCard;
